const HOME_FONT_CSS =
  'https://fonts.googleapis.com/css2?family=Google+Sans+Flex:opsz,wght@6..144,1..1000&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';

const FONT_WAIT_MS = 600;
export const HOME_SHELL_LOADER_FADE_MS = 350;

type VirtupWindow = Window & {
  __virtupHomeFontsReady?: Promise<void>;
};

function loadHomeFontStylesheet(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const existing = document.querySelector(`link[href="${HOME_FONT_CSS}"]`);
  if (existing) {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = HOME_FONT_CSS;
  document.head.appendChild(link);
}

async function waitForFontFaces(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) {
    return;
  }

  if (
    document.fonts.check('700 72px "Google Sans Flex"') &&
    document.fonts.check('400 16px Inter')
  ) {
    return;
  }

  loadHomeFontStylesheet();

  await Promise.race([
    Promise.all([
      document.fonts.load('700 72px "Google Sans Flex"'),
      document.fonts.load('400 16px Inter'),
      document.fonts.ready,
    ]),
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, 400);
    }),
  ]);
}

export function waitForHomeFontsReady(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const win = window as VirtupWindow;
  if (win.__virtupHomeFontsReady) {
    return win.__virtupHomeFontsReady;
  }

  const timeout = new Promise<void>((resolve) => {
    window.setTimeout(resolve, FONT_WAIT_MS);
  });

  win.__virtupHomeFontsReady = Promise.race([waitForFontFaces(), timeout]).catch(() => undefined);
  return win.__virtupHomeFontsReady;
}

export function hideHomeShellStaticHeader(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const staticHeader = document.getElementById('home-shell-static-header');
  if (!staticHeader) {
    return;
  }

  staticHeader.hidden = true;
  staticHeader.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.add('home-shell-static-header--handoff');
}

export function hideHomeShellStaticLayer(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const staticLayer = document.getElementById('home-shell-static-layer');
  if (!staticLayer) {
    return;
  }

  staticLayer.classList.add('home-shell-static-layer--hidden');
  staticLayer.setAttribute('aria-hidden', 'true');
  staticLayer.hidden = true;
  staticLayer.style.pointerEvents = 'none';
}

function releaseHomeShellScrollLock(): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.classList.remove(
    'home-shell-loader-pending',
    'home-shell-fonts-pending'
  );
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
}

function finalizeHideHomeShellInlineLoader(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const loader = document.getElementById('home-shell-inline-loader');
  if (!loader) {
    return;
  }

  loader.style.opacity = '0';
  loader.style.visibility = 'hidden';
  loader.classList.add('home-shell-inline-loader--hidden');
  loader.classList.remove('home-shell-inline-loader--exiting');
  loader.setAttribute('aria-hidden', 'true');
  loader.setAttribute('aria-busy', 'false');
  loader.hidden = true;
  loader.style.pointerEvents = 'none';
}

/** Fades out the inline HTML loader before removing it from view. */
export function fadeOutHomeShellInlineLoader(): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }

  const loader = document.getElementById('home-shell-inline-loader');
  if (!loader || loader.classList.contains('home-shell-inline-loader--hidden')) {
    return Promise.resolve();
  }

  if (loader.classList.contains('home-shell-inline-loader--exiting')) {
    return new Promise((resolve) => {
      const waitForHidden = () => {
        if (loader.classList.contains('home-shell-inline-loader--hidden')) {
          resolve();
          return;
        }
        window.setTimeout(waitForHidden, 50);
      };
      waitForHidden();
    });
  }

  return new Promise((resolve) => {
    loader.classList.add('home-shell-inline-loader--exiting');
    loader.setAttribute('aria-busy', 'false');
    loader.getBoundingClientRect();

    window.setTimeout(() => {
      releaseHomeShellScrollLock();
      finalizeHideHomeShellInlineLoader();
      resolve();
    }, HOME_SHELL_LOADER_FADE_MS);
  });
}

/** Hides the inline HTML loader shown before React hydrates. */
export function hideHomeShellInlineLoader(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const loader = document.getElementById('home-shell-inline-loader');
  if (loader?.classList.contains('home-shell-inline-loader--exiting')) {
    return;
  }

  releaseHomeShellScrollLock();
  finalizeHideHomeShellInlineLoader();
}

/** Applies shell-ready classes and visual swaps under the opaque loader (before fade). */
export function prepareHomeLandingShellReveal(): void {
  if (typeof document === 'undefined') {
    return;
  }

  markHomeLandingFontsReady();
  document.documentElement.classList.remove('fa-fonts-pending');
  document.documentElement.classList.add('home-landing-shell-ready');
  releaseHomeShellEntry();
}

/** @deprecated Use prepareHomeLandingShellReveal before loader fade. */
export function finalizeHomeLandingShellReveal(): void {
  prepareHomeLandingShellReveal();
}

/** Removes home-landing shell document classes so other routes keep their theme. */
export function clearHomeLandingShellDocumentState(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const html = document.documentElement;
  html.classList.remove(
    'home-landing-shell-ready',
    'home-landing-fonts-ready',
    'home-shell-loader-pending',
    'home-shell-fonts-pending'
  );
  html.style.overflow = '';
  html.style.background = '';
  html.style.colorScheme = '';
  document.body.style.overflow = '';
  document.body.style.background = '';
  document.querySelector('.home-landing-page')?.classList.remove('home-landing-page--fonts-ready');
}

/** Clears home-shell html bootstrap so body theme classes work on all routes. */
export function releaseHomeShellEntry(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const html = document.documentElement;
  html.classList.remove('home-shell-entry');
  html.style.background = '';
  html.style.colorScheme = '';

  document.getElementById('virtup-shell-first-paint')?.remove();
  document.getElementById('virtup-home-color-scheme')?.remove();
}

/** Clears shell bootstrap + landing document state when leaving home routes. */
export function releaseHomeShellForAppRoute(): void {
  releaseHomeShellEntry();
  clearHomeLandingShellDocumentState();
}

/** Clears all home-shell HTML overlays that can block scroll, layout, and clicks. */
export async function releaseHomeShellOverlays(options?: {
  animateLoader?: boolean;
}): Promise<void> {
  if (typeof document === 'undefined') {
    return;
  }

  hideHomeShellStaticLayer();

  if (options?.animateLoader) {
    await fadeOutHomeShellInlineLoader();
  } else {
    hideHomeShellInlineLoader();
  }

  releaseHomeShellEntry();
}

export function markHomeLandingFontsReady(): void {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.documentElement.classList.contains('home-landing-fonts-ready')) {
    return;
  }

  document.documentElement.classList.add('home-landing-fonts-ready');
  document.querySelector('.home-landing-page')?.classList.add('home-landing-page--fonts-ready');
}
